import { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import ProductHeader from "./ProductHeader/ProductHeader.jsx";
import isEmpty from "lodash/isEmpty";
import { FormattedMessage, FormattedPlural, useIntl } from "react-intl";
import { ficheProduitPropTypes, photoPropTypes, videoPropTypes } from "app/utils/propTypes";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { BRANDS, QUOTATION_PRODUCT_EXPERIENCE } from "app/constants";
import List from "app/pages/.shared/List";
import PropTypes from "prop-types";
import SocialBarPopover from "app/pages/.shared/social/SocialBarPopover";
import "./FicheProduit.scss";
import ProductActionButtonMobile from "app/pages/FicheProduit/components/ProductActionButtonMobile/ProductActionButtonMobile";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import Collapse from "app/components/ui/collapse/Collapse";
import LookAlikeProductsContainer from "app/pages/.shared/LookAlikeProduct/LookAlikeProductsContainer";
import OffersOverview from "app/pages/.shared/OffersOverview/OffersOverview";
import AvailableDurations from "app/pages/.shared/AvailableDurations";
import BookingBannerPlaceholderContainer from "app/pages/Booking/BookingBannerPlaceholder/BookingBannerPlaceholderContainer";
import NegociatedList from "app/pages/FicheProduit/NegociatedList/NegociatedList";
import { useIntersectionObserver } from "app/hooks";
import AsideOffersOverview from "app/pages/FicheProduit/AsideOffersOverview/AsideOffersOverview";
import { scrollToElement } from "app/utils/scroller";
import loadable from "@loadable/component";
import IconGoToTopArrow from "app/pages/.shared/static/icons/IconGoToTopArrow";
import AppGlobalsContext from "app/AppGlobalsContext";
import CondensedBookWithConfidence from "app/pages/.shared/BookWithConfidence/CondensedBookWithConfidence";
import HeaderContainer from "app/pages/Header/HeaderContainer";
import AdditionalSections from "app/pages/FicheProduit/components/AdditionalSections/AdditionalSections";
import FullyCustomizableNote from "app/pages/.shared/FullyCustomizableNote/FullyCustomizableNote";
import PrintHeader from "./PrintHeader/PrintHeader";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";
import TotalCreditBannerContainer from "app/pages/FicheProduit/components/Credit/TotalCreditBannerContainer";
import { redirectToShopUrl } from "app/utils/utils";
import { getIsDubaiProduct, generateHotelName } from "app/utils/productUtils";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory.jsx";
import ReasonToBookBlockContainer from "app/pages/.shared/ReasonsToBook/ReasonToBookBlock/ReasonToBookBlockContainer";
import FicheProduitHeadMetadata from "app/pages/FicheProduit/components/FicheProduitHeadMetadata";
import TrustpilotContainer from "app/features/booking/components/Trustpilot/TrustpilotContainer";
import ContactUs from "app/pages/.shared/ContactUs/ContactUs";
import AccommodationSection from "app/pages/FicheProduit/components/Accommodation/AccommodationSection";
import AccommodationTypes from "app/pages/FicheProduit/components/Accommodation/AccommodationTypes";
import Amenities from "app/pages/FicheProduit/components/Amenities/Amenities";
import RestaurantSection from "app/pages/FicheProduit/components/Restaurant/RestaurantSection";
import Activities from "app/pages/FicheProduit/components/Activities/Activities";
import TransportPartnership from "app/pages/FicheProduit/components/TransportPartnership/TransportPartnership";
import Destination from "app/pages/FicheProduit/components/Destination/Destination";
import UsefulInformation from "app/pages/FicheProduit/components/UsefulInformation/UsefulInformation";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import LookAlikeSearchContentContainer from "app/pages/.shared/LookAlikeProduct/LookAlikeSearchContentContainer";
import ProductBadge from "app/features/product/components/ProductBadge/ProductBadge";
import PaymentDescriptionContainer from "app/pages/.shared/DynamicBlocks/blocks/PaymentDescriptionBlock/PaymentDescriptionContainer";
import SearchEngineCreditBannerContainer from "app/pages/PrePackage/components/SearchEngineCreditBanner/SearchEngineCreditBannerContainer";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import { ICON_SIZES } from "app/constants";
import ArrowDownIcon from "app/icons/navigation/ArrowDownIcon.jsx";
import PhotoFilledIcon from "app/icons/navigation/PhotoFilledIcon.jsx";
import ShareFilledIcon from "app/icons/navigation/ShareFilledIcon.jsx";
import { useSlideshow } from "app/components/ui/SlideShow/useSlideshow";
import { useFsFlag } from "@flagship.io/react-sdk";
import { useBreadcrumbOrigin, BreadcrumbsContainer } from "app/features/breadcrumb";

const SearchEngineContainer = loadable(
	() =>
		import(
			/* webpackChunkName: "product-page-search-engine" */ "app/pages/PrePackage/SearchEngineContainer.jsx"
		)
);

const PhotoCarousel = loadable(
	() =>
		import(
			/* webpackChunkName: "product-page-photo-caroussel" */ "app/pages/.shared/PhotoCarousel.jsx"
		)
);

const SlideShow = loadable(
	() =>
		import(
			/* webpackChunkName: "product-page-slideshow" */ "app/components/ui/SlideShow/SlideShow.jsx"
		)
);

const HERO_IMG_SIZES = {
	medium: { height: 460 },
	large: { height: 460 },
	xlarge: { height: 460 },
};

const HOTEL_PANEL_INDEX = "0";
const ROOMS_PANEL_INDEX = "1";
const RESTURANTS_PANEL_INDEX = "2";
const ACTIVITIES_PANEL_INDEX = "3";

const FicheProduit = ({
	resolution,
	ficheProduit = {},
	prePackageOffers = [],
	negociated = [],
	allAvailableDurations,
	currentShop = "",
	partnerCode,
	mobileSlideshowPhotos = [],
	desktopSlideShowPhotos = [],
	videos = [],
	shouldFetchLookAlikeProduts,
	specialOffer = {},
	isWhiteLabel,
	googleAPIKey,
	showPfsMessage,
	updateBooking,
	productPriceValue,
	hidePhoneContactUs,
	isPaymentDescriptionBlockExist,
	emiratesPassReinsurance,
}) => {
	const { track } = useAnalytics();
	const { formatMessage } = useIntl();
	const originCrumb = useBreadcrumbOrigin();
	const ppContentFlags = useFsFlag("pp_content_accordions_closed");
	const ppContentFlagsValues = ppContentFlags.getValue(false);

	const [showStickyHeader, toggleStickyHeaderButton] = useState(false);
	const [showScrollTopButton, toggleScrollTopButton] = useState(false);
	const [showStickyOffersOverview, toggleOfferOverview] = useState(false);
	const searchEngineRef = useRef();

	const { shop, brand, envVars } = useContext(AppGlobalsContext);
	const proxyBasenamePrefix = envVars?.PROXY_BASENAME_PREFIX;

	const { isSlideshowOpened, openSlideshow, closeSlideshow } = useSlideshow();

	const isBrandSV = brand === BRANDS.SV;
	const isDubaiProduct = getIsDubaiProduct(ficheProduit?.geography);
	const showEmiratesPassItem = brand === BRANDS.EK && isDubaiProduct;

	const [showStickyRef, isStickyIntersecting, stickyEntry] = useIntersectionObserver();

	const [topRef, isTopIntersecting, topEntry] = useIntersectionObserver();

	const stickyClassname = classNames("product-page__sticky", {
		"product-page__sticky--top": showStickyOffersOverview,
	});

	const openPhotosSlideshow = useCallback(() => {
		track("open_photos_carousel_desktop_click");
		openSlideshow();
	}, []);

	useEffect(() => {
		const top = stickyEntry?.boundingClientRect?.top ?? 0;
		const rootTop = stickyEntry?.rootBounds?.top ?? 0;
		const rootBottom = stickyEntry?.rootBounds?.bottom ?? window.innerHeight;

		const leavingFromTop = !isStickyIntersecting && top < rootTop;
		const leavingFromBottom = !isStickyIntersecting && top > rootBottom;

		toggleOfferOverview(leavingFromTop);

		if (leavingFromTop) {
			toggleScrollTopButton(true);
		} else if (leavingFromBottom) {
			toggleScrollTopButton(false);
		}
	}, [isStickyIntersecting, stickyEntry]);

	useEffect(() => {
		const top = topEntry?.boundingClientRect?.top ?? 0;
		const rootTop = topEntry?.rootBounds?.top ?? 0;

		const leavingFromTop = !isTopIntersecting && top < rootTop;

		if (isTopIntersecting) {
			toggleStickyHeaderButton(false);
		} else if (leavingFromTop) {
			toggleStickyHeaderButton(true);
		}
	}, [isTopIntersecting, topEntry]);

	useEffect(() => {
		if (ficheProduit?.notFound) {
			const [currentLanguageCode, currentCountryCode] = currentShop.split("-");
			redirectToShopUrl({
				language: currentLanguageCode,
				country: currentCountryCode,
				proxyBasenamePrefix,
			});
		}
	}, [ficheProduit.notFound, currentShop, proxyBasenamePrefix]);

	const scrollToSearchEngine = useCallback(() => {
		scrollToElement({
			element: searchEngineRef.current,
			offset: 0,
			disableSmoothScroll: false,
		});
	}, []);

	const containsVideo = videos.length > 0;
	const isDealExpired =
		ficheProduit?.expiration?.hour === 0 && ficheProduit?.expiration?.day === 0;

	const isScreenBelowMedium = resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL;
	const offerWithTransportLabel = prePackageOffers[0]?.pricing?.label;

	const scrollTopButtonClass = classNames({
		"scroll-top-button": true,
		"scroll-top-button--visible": showScrollTopButton,
	});

	const stickyHeaderClass = classNames({
		"product-page__sticky-header": true,
		"product-page__sticky-header--visible": showStickyHeader,
	});

	const handleScrollToTopClick = useCallback(() => {
		window.scrollTo({ behavior: "smooth", left: 0, top: 0 });
	}, []);

	if (ficheProduit.notFound) {
		return null;
	}

	const closeAccordionsByDefault = ppContentFlagsValues;

	const BLOCKS_TO_EXPAND_BY_DEFAULT = closeAccordionsByDefault
		? []
		: [HOTEL_PANEL_INDEX, ROOMS_PANEL_INDEX, RESTURANTS_PANEL_INDEX];

	if (!closeAccordionsByDefault && (!ficheProduit.activity || !isEmpty(ficheProduit.activity))) {
		BLOCKS_TO_EXPAND_BY_DEFAULT.push(ACTIVITIES_PANEL_INDEX);
	}

	if (!closeAccordionsByDefault && (ficheProduit?.itinerary?.blocks?.length ?? 0) > 0) {
		BLOCKS_TO_EXPAND_BY_DEFAULT.push(`${Number(ACTIVITIES_PANEL_INDEX) + 1}`);
	}

	const productExperience = ficheProduit?.productExperience;
	const isHotelOrResidenceAccommodation =
		productExperience === QUOTATION_PRODUCT_EXPERIENCE.HOTEL ||
		productExperience === QUOTATION_PRODUCT_EXPERIENCE.RESIDENCE;

	const restaurantSectionHeadline = isHotelOrResidenceAccommodation ? (
		<FormattedMessage id={"product.page.restaurant.headline"} />
	) : (
		ficheProduit?.restaurant?.headline
	);

	const activitiesLabel = isHotelOrResidenceAccommodation ? (
		<FormattedMessage id={"product.page.activities.label"} />
	) : (
		ficheProduit?.activity?.label
	);

	const accommodationTypeHeadline = isHotelOrResidenceAccommodation ? (
		<FormattedMessage id={"product.page.accommodation.type.headline"} />
	) : (
		ficheProduit?.accommodation?.type?.headline
	);

	const productPageHeroClass = classNames("product-page__hero", {
		"product-page__hero--expired-deal": isDealExpired,
	});

	const crumbs = [
		{ label: formatMessage({ id: "faq.breadcrumb.home" }), to: "/" },
		...(originCrumb ? [originCrumb] : []),
		{ label: generateHotelName(ficheProduit.name, ficheProduit.category) },
	];

	return (
		<div className="product-page">
			<PrintHeader showPfsMessage={showPfsMessage} />
			<FicheProduitHeadMetadata
				ficheProduit={ficheProduit}
				desktopSlideShowPhotos={desktopSlideShowPhotos}
			/>

			<BreadcrumbsContainer items={crumbs} className="product-page__breadcrumbs" />

			{isWhiteLabel && isScreenBelowMedium && (
				<div className={stickyHeaderClass}>
					<HeaderContainer
						showMenuItems={true}
						showDialPhone={false}
						showBackBtn={true}
						showMarketingBanner={false}
					/>
				</div>
			)}
			{!isScreenBelowMedium && isSlideshowOpened && (
				<SlideShow
					isOpened={isSlideshowOpened}
					close={closeSlideshow}
					photos={desktopSlideShowPhotos}
				/>
			)}
			<div className={productPageHeroClass} data-testid="product-page-hero">
				{isDealExpired && (
					<Typography
						variant={TYPOGRAPHY_VARIANTS.XL3}
						variantMD={TYPOGRAPHY_VARIANTS.XL9}
						isBold
						className="hero__text--expired-deal"
					>
						<FormattedMessage id="product.deal.expired.label" />
					</Typography>
				)}

				{/* HERO MEDIA TYPE VIEWS ON MOBILE */}
				{isScreenBelowMedium && mobileSlideshowPhotos.length > 0 && (
					<PhotoCarousel photos={mobileSlideshowPhotos} resolution={resolution} />
				)}
				{/* HERO MEDIA TYPE VIEWS ON DESKTOP */}
				{!isScreenBelowMedium && desktopSlideShowPhotos && desktopSlideShowPhotos[0] && (
					<div className="hero-photo" onClick={openPhotosSlideshow}>
						<div className="hero__image">
							<ProgressiveImg
								sizes={HERO_IMG_SIZES}
								src={desktopSlideShowPhotos[0].url}
								thumbnail={desktopSlideShowPhotos[0].thumbnail}
								quality="q_auto:best"
								backgroundPosition={"center"}
								productUri={ficheProduit?.uri}
								cover
								disableLazyLoad
								fetchPriority="high"
							/>
						</div>
					</div>
				)}

				{isScreenBelowMedium ? (
					<>
						<div className="toolbar">
							<div className="toolbar__item">
								<SocialBarPopover
									position="bottom"
									buttonLabel={
										<ShareFilledIcon
											size={ICON_SIZES.MD}
											style={{ marginRight: "8px" }}
										/>
									}
									urlToShare={
										typeof window !== "undefined"
											? window.location.origin + window.location.pathname
											: ""
									}
								/>
							</div>
						</div>
					</>
				) : (
					<div className="product-page__toolbar">
						<div className="product-page__container">
							<div className="toolbar">
								<div className="toolbar__item" onClick={openPhotosSlideshow}>
									<PhotoFilledIcon
										size={ICON_SIZES.MD}
										style={{ marginRight: "8px" }}
									/>
									<FormattedPlural
										value={desktopSlideShowPhotos.length}
										one={
											<FormattedMessage
												id="product.page.photo.button.label.singular"
												values={{
													length: desktopSlideShowPhotos.length,
												}}
											/>
										}
										other={
											<FormattedMessage
												id="product.page.photo.button.label.plural"
												values={{
													length: desktopSlideShowPhotos.length,
												}}
											/>
										}
									/>
								</div>

								<div className="toolbar__item">
									<SocialBarPopover
										position="top"
										buttonLabel={
											<div
												style={{
													display: "flex",
													alignItems: "center",
												}}
											>
												<ShareFilledIcon
													size={ICON_SIZES.MD}
													style={{ marginRight: "8px" }}
												/>
												<FormattedMessage id="product.page.map.share.label" />
											</div>
										}
										urlToShare={
											typeof window !== "undefined"
												? window.location.origin + window.location.pathname
												: ""
										}
									/>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
			<div className="product-page__header">
				<div className="product-page__container">
					<div className="product-page__product-header" ref={topRef}>
						<ProductHeader
							name={ficheProduit.name}
							category={ficheProduit.category}
							categoryType={ficheProduit.categoryType}
							region={ficheProduit.region}
							resort={ficheProduit.resort}
							country={ficheProduit.country}
						/>

						<ProductRatingContainer
							locationName={
								<ProductNameWithCategory
									name={ficheProduit.name}
									category={ficheProduit.category}
								/>
							}
							tripAdvisor={ficheProduit?.tripAdvisor}
							holidayCheck={{ ...ficheProduit?.holidaycheck, size: "small" }}
						/>

						{ficheProduit.badges && ficheProduit.badges.length > 0 && (
							<div className="product-page__badges">
								{ficheProduit.badges?.map((badge, id) => {
									return <ProductBadge key={id} label={badge.label} />;
								})}
							</div>
						)}
					</div>

					{isScreenBelowMedium && (
						<div className="product-page__offers">
							{isDealExpired && (
								<LookAlikeSearchContentContainer productId={ficheProduit?.id} />
							)}

							{isPaymentDescriptionBlockExist ? (
								<div className="product-page__payment-description">
									<PaymentDescriptionContainer />
								</div>
							) : (
								<TotalCreditBannerContainer />
							)}

							{Number(productPriceValue) > 0 && (
								<div className="product-page__offers-overview">
									<OffersOverview
										offers={prePackageOffers}
										updateBooking={updateBooking}
										allAvailableDurations={allAvailableDurations}
										productPriceValue={productPriceValue}
									/>

									<div className="product-page__available-durations">
										<AvailableDurations durations={allAvailableDurations} />
									</div>
								</div>
							)}
							{(negociated?.length > 0 || showEmiratesPassItem) && (
								<div className="product-page__negociated">
									<NegociatedList
										sizeIcon={ICON_SIZES.XS}
										negociatedList={negociated}
										specialOffer={specialOffer}
										offerWithTransportLabel={offerWithTransportLabel}
										showEmiratesPassItem={showEmiratesPassItem}
									/>

									<FullyCustomizableNote />
								</div>
							)}

							{isPaymentDescriptionBlockExist && (
								<SearchEngineCreditBannerContainer />
							)}

							<BookingBannerPlaceholderContainer disableFlashySpecialOffer />
						</div>
					)}

					{!isScreenBelowMedium && (
						<aside className="product-page__aside" ref={searchEngineRef}>
							<div className="product-page__container product-page__container--aside">
								{!isDealExpired ? (
									<div className="product-page__search-engine">
										<SearchEngineContainer />
									</div>
								) : (
									<LookAlikeSearchContentContainer productId={ficheProduit?.id} />
								)}
								{shop === "en-GB" && (
									<div className="product-page__atol-abta">
										<CondensedBookWithConfidence />
									</div>
								)}
								<div className="product-page__trustpilot">
									<TrustpilotContainer />
								</div>
								{!isDealExpired && (
									<div className="product-page__contact" ref={showStickyRef}>
										<ContactUs
											productReference={ficheProduit.id}
											hidePhone={hidePhoneContactUs}
										/>
									</div>
								)}
							</div>
						</aside>
					)}
				</div>
			</div>
			<main className="product-page__main">
				<div className="product-page__container product-page__container--main">
					<div>
						{isScreenBelowMedium ? (
							<Collapse
								key={closeAccordionsByDefault}
								defaultActiveKey={BLOCKS_TO_EXPAND_BY_DEFAULT}
								expandIcon={({ isActive }) => {
									return (
										<ArrowDownIcon
											style={{
												transition: "transform .2s ease",
												transform: `rotate(${isActive ? 180 : 0}deg)`,
											}}
											className="icon"
										/>
									);
								}}
							>
								<Collapse.Panel
									headerClass="fiche-produit-item__header"
									header={
										<Typography
											variant={TYPOGRAPHY_VARIANTS.XL}
											variantMD={TYPOGRAPHY_VARIANTS.XL4}
											isBold
											className="fiche-produit-item__title"
											component="h2"
										>
											{ficheProduit?.accommodation?.intro?.headline}
										</Typography>
									}
								>
									<div className="fiche-produit-item__body">
										<AccommodationSection
											videoUrl={containsVideo ? videos[0].url : undefined}
											videoType={
												containsVideo ? videos[0].videoType : undefined
											}
											tripAdvisor={ficheProduit?.tripAdvisor ?? {}}
											holidayCheck={ficheProduit?.holidaycheck ?? {}}
											name={ficheProduit.name}
											category={ficheProduit.category}
											categoryType={ficheProduit.categoryType}
											accommodation={ficheProduit.accommodation}
											sanitaryInformations={ficheProduit.sanitaryInformations}
											gpsLat={ficheProduit?.gps?.lat}
											gpsLong={ficheProduit?.gps?.lon}
											interactiveMap={ficheProduit.interactiveMap}
											googleAPIKey={googleAPIKey}
										/>
									</div>
								</Collapse.Panel>

								{ficheProduit?.accommodation?.amenities?.headline &&
									ficheProduit?.accommodation?.amenities?.groupedAmenities
										?.length > 0 && (
										<Collapse.Panel
											headerClass="fiche-produit-item__header"
											header={
												<Typography
													variant={TYPOGRAPHY_VARIANTS.XL}
													variantMD={TYPOGRAPHY_VARIANTS.XL4}
													isBold
													className="fiche-produit-item__title"
													component="h2"
												>
													{
														ficheProduit?.accommodation?.amenities
															?.headline
													}
												</Typography>
											}
										>
											<div className="fiche-produit-item__body">
												<Amenities
													amenities={
														ficheProduit?.accommodation?.amenities
															?.groupedAmenities
													}
												/>
											</div>
										</Collapse.Panel>
									)}

								{(ficheProduit?.itinerary?.blocks?.length ?? 0) > 0 && (
									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<Typography
												variant={TYPOGRAPHY_VARIANTS.XL}
												variantMD={TYPOGRAPHY_VARIANTS.XL4}
												isBold
												className="fiche-produit-item__title"
												component="h2"
											>
												{ficheProduit?.itinerary?.headline}
											</Typography>
										}
									>
										<div className="fiche-produit-item__body">
											<AdditionalSections
												blocks={ficheProduit?.itinerary?.blocks}
											/>
										</div>
									</Collapse.Panel>
								)}
								{ficheProduit?.accommodation?.type?.headline && (
									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<Typography
												variant={TYPOGRAPHY_VARIANTS.XL}
												variantMD={TYPOGRAPHY_VARIANTS.XL4}
												isBold
												className="fiche-produit-item__title"
												component="h2"
											>
												{ficheProduit?.accommodation?.type?.headline}
											</Typography>
										}
									>
										<div className="fiche-produit-item__body">
											<AccommodationTypes
												paragraphs={
													ficheProduit?.accommodation?.type?.paragraphs
												}
												photo={ficheProduit?.accommodation?.type?.photo}
												accommodationTypes={
													ficheProduit?.accommodation?.type
														?.accommodationTypes
												}
												facilities={
													ficheProduit?.accommodation?.type?.facilities
												}
												productUri={ficheProduit?.uri}
											/>
										</div>
									</Collapse.Panel>
								)}
								{ficheProduit?.restaurant?.headline && !isBrandSV && (
									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<Typography
												variant={TYPOGRAPHY_VARIANTS.XL}
												variantMD={TYPOGRAPHY_VARIANTS.XL4}
												isBold
												className="fiche-produit-item__title"
												component="h2"
											>
												{ficheProduit?.restaurant?.headline}
											</Typography>
										}
									>
										<div className="fiche-produit-item__body">
											<RestaurantSection
												intro={ficheProduit?.restaurant?.intro}
												restaurants={ficheProduit?.restaurant?.restaurants}
												facilities={ficheProduit?.restaurant?.facilities}
												boards={ficheProduit.boards}
											/>
										</div>
									</Collapse.Panel>
								)}
								{(!ficheProduit.activity || !isEmpty(ficheProduit.activity)) && (
									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<Typography
												variant={TYPOGRAPHY_VARIANTS.XL}
												variantMD={TYPOGRAPHY_VARIANTS.XL4}
												isBold
												className="fiche-produit-item__title"
												component="h2"
											>
												{ficheProduit?.activity?.label}
											</Typography>
										}
									>
										<div className="fiche-produit-item__body">
											<Activities
												intro={ficheProduit?.activity?.intro}
												facilities={ficheProduit?.activity?.facilities}
												activities={ficheProduit?.activity?.activities}
												description={ficheProduit?.activity?.description}
											/>
										</div>
									</Collapse.Panel>
								)}

								{ficheProduit.additionalSections &&
									ficheProduit.additionalSections.map(additionalSection => {
										return (
											<Collapse.Panel
												key={additionalSection.headline}
												headerClass="fiche-produit-item__header"
												header={
													<Typography
														variant={TYPOGRAPHY_VARIANTS.XL}
														variantMD={TYPOGRAPHY_VARIANTS.XL4}
														isBold
														className="fiche-produit-item__title"
														component="h2"
													>
														{additionalSection.headline}
													</Typography>
												}
											>
												<div className="fiche-produit-item__body">
													<AdditionalSections
														moreDetails={additionalSection.moreDetails}
														blocks={additionalSection.blocks}
													/>
												</div>
											</Collapse.Panel>
										);
									})}

								{!isEmpty(ficheProduit.destination) && (
									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<Typography
												variant={TYPOGRAPHY_VARIANTS.XL}
												variantMD={TYPOGRAPHY_VARIANTS.XL4}
												isBold
												className="fiche-produit-item__title"
												component="h2"
											>
												<FormattedMessage
													id="destination.discover"
													values={{
														destination:
															ficheProduit?.destination?.label,
													}}
												/>
											</Typography>
										}
									>
										<div className="fiche-produit-item__body">
											<Destination
												paragraphs={ficheProduit?.destination?.paragraphs}
												photo={ficheProduit?.destination?.photo}
												video={ficheProduit?.destination?.video}
												showVideoOnModal={!isScreenBelowMedium}
											/>
										</div>
									</Collapse.Panel>
								)}

								{ficheProduit?.transportPartnership?.headline && (
									<Collapse.Panel
										headerClass="fiche-produit-item__header"
										header={
											<Typography
												variant={TYPOGRAPHY_VARIANTS.XL}
												variantMD={TYPOGRAPHY_VARIANTS.XL4}
												isBold
												className="fiche-produit-item__title"
												component="h2"
											>
												{ficheProduit?.transportPartnership?.headline}
											</Typography>
										}
									>
										<div className="fiche-produit-item__body">
											<TransportPartnership
												intro={ficheProduit.transportPartnership.intro}
												logo={ficheProduit.transportPartnership.logo}
												paragraphs={
													ficheProduit.transportPartnership.paragraphs
												}
												photo={ficheProduit.transportPartnership.photo}
												video={ficheProduit.transportPartnership.video}
												emiratesPass={
													showEmiratesPassItem
														? emiratesPassReinsurance
														: undefined
												}
											/>
										</div>
									</Collapse.Panel>
								)}

								<Collapse.Panel
									headerClass="fiche-produit-item__header"
									header={
										<Typography
											variant={TYPOGRAPHY_VARIANTS.XL}
											variantMD={TYPOGRAPHY_VARIANTS.XL4}
											isBold
											className="fiche-produit-item__title"
											component="h2"
										>
											<FormattedMessage id="useful.information.title" />
										</Typography>
									}
								>
									<div className="fiche-produit-item__body">
										<UsefulInformation
											partnerCode={partnerCode}
											usefulInformations={ficheProduit.usefulInformations}
										/>
									</div>
								</Collapse.Panel>
							</Collapse>
						) : (
							<>
								<TotalCreditBannerContainer />

								{(negociated?.length > 0 || showEmiratesPassItem) && (
									<div className="product-page__bloc product-page__bloc--highlighted">
										<NegociatedList
											sizeIcon={ICON_SIZES.MD}
											negociatedList={negociated}
											specialOffer={specialOffer}
											offerWithTransportLabel={offerWithTransportLabel}
											showEmiratesPassItem={showEmiratesPassItem}
										/>
										<FullyCustomizableNote />
									</div>
								)}

								<div className="product-page__bloc">
									<AccommodationSection
										headline={ficheProduit?.accommodation?.intro?.headline}
										videoUrl={containsVideo ? videos[0].url : undefined}
										videoType={containsVideo ? videos[0].videoType : undefined}
										displayVideo={!isBrandSV}
										tripAdvisor={ficheProduit?.tripAdvisor ?? {}}
										holidayCheck={ficheProduit?.holidaycheck ?? {}}
										name={ficheProduit.name}
										category={ficheProduit.category}
										categoryType={ficheProduit.categoryType}
										accommodation={ficheProduit.accommodation}
										sanitaryInformations={ficheProduit.sanitaryInformations}
										gpsLat={ficheProduit?.gps?.lat}
										gpsLong={ficheProduit?.gps?.lon}
										interactiveMap={ficheProduit.interactiveMap}
										googleAPIKey={googleAPIKey}
									/>
								</div>

								{(ficheProduit?.itinerary?.blocks?.length ?? 0) > 0 && (
									<div className="product-page__bloc">
										<AdditionalSections
											headline={ficheProduit?.itinerary?.headline}
											moreDetails={ficheProduit?.itinerary?.moreDetails}
											blocks={ficheProduit?.itinerary?.blocks}
										/>
									</div>
								)}
								{ficheProduit?.accommodation?.type?.headline && (
									<div className="product-page__bloc">
										<AccommodationTypes
											headline={accommodationTypeHeadline}
											paragraphs={
												ficheProduit?.accommodation?.type?.paragraphs
											}
											photo={ficheProduit?.accommodation?.type?.photo}
											accommodationTypes={
												ficheProduit?.accommodation?.type
													?.accommodationTypes
											}
											facilities={
												ficheProduit?.accommodation?.type?.facilities
											}
											productUri={ficheProduit?.uri}
										/>
									</div>
								)}

								{ficheProduit?.accommodation?.amenities?.headline &&
									ficheProduit?.accommodation?.amenities?.groupedAmenities
										?.length > 0 && (
										<div className="product-page__bloc">
											<Amenities
												headline={
													ficheProduit?.accommodation?.amenities?.headline
												}
												amenities={
													ficheProduit?.accommodation?.amenities
														?.groupedAmenities
												}
											/>
										</div>
									)}

								{ficheProduit?.restaurant?.headline && !isBrandSV && (
									<div className="product-page__bloc">
										<RestaurantSection
											headline={restaurantSectionHeadline}
											intro={ficheProduit?.restaurant?.intro}
											restaurants={ficheProduit?.restaurant?.restaurants}
											facilities={ficheProduit?.restaurant?.facilities}
											boards={ficheProduit.boards}
										/>
									</div>
								)}

								{(!ficheProduit.activity || !isEmpty(ficheProduit.activity)) && (
									<div className="product-page__bloc">
										<Activities
											label={activitiesLabel}
											intro={ficheProduit?.activity?.intro}
											facilities={ficheProduit?.activity?.facilities}
											activities={ficheProduit?.activity?.activities}
											description={ficheProduit?.activity?.description}
										/>
									</div>
								)}

								{ficheProduit.additionalSections &&
									ficheProduit.additionalSections.map(additionalSection => {
										return (
											<div
												className="product-page__bloc"
												key={additionalSection.headline}
											>
												<AdditionalSections
													headline={additionalSection.headline}
													blocks={additionalSection.blocks}
												/>
											</div>
										);
									})}

								{!isEmpty(ficheProduit.destination) && (
									<div className="product-page__bloc">
										<Destination
											title={<FormattedMessage id="destination.discover" />}
											paragraphs={ficheProduit?.destination?.paragraphs}
											photo={ficheProduit?.destination?.photo}
											video={ficheProduit?.destination?.video}
											showVideoOnModal={!isScreenBelowMedium}
										/>
									</div>
								)}

								{!isEmpty(ficheProduit.destination?.importantInfo) && (
									<div className="product-page__bloc">
										<Destination
											title={
												<FormattedMessage id="product.page.destination.important.information.title" />
											}
											paragraphs={ficheProduit?.destination?.importantInfo}
										/>
									</div>
								)}

								{ficheProduit?.transportPartnership?.headline && (
									<div className="product-page__bloc">
										<TransportPartnership
											headline={ficheProduit.transportPartnership.headline}
											intro={ficheProduit.transportPartnership.intro}
											logo={ficheProduit.transportPartnership.logo}
											paragraphs={
												ficheProduit.transportPartnership.paragraphs
											}
											photo={ficheProduit.transportPartnership.photo}
											video={ficheProduit.transportPartnership.video}
											emiratesPass={
												showEmiratesPassItem
													? emiratesPassReinsurance
													: undefined
											}
										/>
									</div>
								)}
							</>
						)}
					</div>

					{!isScreenBelowMedium && !isDealExpired && (
						<div>
							<aside className={stickyClassname}>
								<div className="product-page__sticky-content">
									<AsideOffersOverview
										offers={prePackageOffers}
										onClick={scrollToSearchEngine}
										itemClickable={false}
										productPriceValue={productPriceValue}
									/>

									{shop === "en-GB" && (
										<div className="product-page__atol-abta">
											<CondensedBookWithConfidence />
										</div>
									)}

									<div className="product-page__trustpilot">
										<TrustpilotContainer />
									</div>

									<div className="product-page__contact">
										<ContactUs
											productReference={ficheProduit.id}
											hidePhone={hidePhoneContactUs}
										/>
									</div>
								</div>
							</aside>
						</div>
					)}
				</div>

				<footer className="product-page__footer">
					{!isDealExpired && isScreenBelowMedium && (
						<>
							<div className="product-page__trustpilot">
								<TrustpilotContainer />
							</div>
							<div className="product-page__contact">
								<ContactUs
									productReference={ficheProduit.id}
									hidePhone={hidePhoneContactUs}
								/>
							</div>
						</>
					)}

					{shouldFetchLookAlikeProduts && !isDealExpired && (
						<div className="product-page__lookalike">
							<LookAlikeProductsContainer productId={ficheProduit.id} />
						</div>
					)}
				</footer>
			</main>
			<div className="product-page__bottom-bar">
				{!isDealExpired && isScreenBelowMedium && <ProductActionButtonMobile />}
			</div>
			<div className="product-page__included">
				{prePackageOffers.map(offer => {
					const offerPricingType = offer?.pricing?.type;

					return (
						<div key={offerPricingType} className="product-page__included__item">
							<div className="included">
								<div className="included__header">{offer?.pricing?.label}</div>
								<div className="included__list">
									<div className="included__title">
										<FormattedMessage id="price.detail.included.title">
											{msg => <span>{`${msg} :`}</span>}
										</FormattedMessage>
									</div>
									<List
										items={ficheProduit?.included?.[offerPricingType]}
										enableHTMLFormatting={true}
									/>
								</div>
								<div className="included__list">
									<div className="included__title">
										<FormattedMessage id="price.detail.not.included.title">
											{msg => <span>{`${msg} :`}</span>}
										</FormattedMessage>
									</div>
									<List
										items={ficheProduit?.notIncluded?.[offerPricingType]}
										enableHTMLFormatting={true}
									/>
								</div>
							</div>
						</div>
					);
				})}
			</div>
			{!isEmpty(ficheProduit.usefulInformations) && !isScreenBelowMedium && (
				<div className="product-page__content">
					<div id="useful-information" className="product-page__information">
						<Collapse
							defaultActiveKey={["0"]}
							expandIcon={({ isActive }) => {
								return (
									<ArrowDownIcon
										style={{
											transition: "transform .2s ease",
											transform: `rotate(${isActive ? 180 : 0}deg)`,
										}}
									/>
								);
							}}
						>
							<Collapse.Panel
								headerClass="fiche-produit-item__header"
								header={
									<Typography
										variant={TYPOGRAPHY_VARIANTS.XL}
										variantMD={TYPOGRAPHY_VARIANTS.XL4}
										isBold
										className="fiche-produit-item__title"
										component="h2"
									>
										<FormattedMessage id="useful.information.title" />
									</Typography>
								}
							>
								<div className="fiche-produit-item__body">
									<UsefulInformation
										usefulInformations={ficheProduit.usefulInformations}
									/>
								</div>
							</Collapse.Panel>
						</Collapse>
					</div>
				</div>
			)}

			<div className="product-page__rtb">
				<ReasonToBookBlockContainer />
			</div>

			<PushNotificationContainer />
			<span className={scrollTopButtonClass} onClick={handleScrollToTopClick}>
				<IconGoToTopArrow width={50} height={50} />
			</span>
		</div>
	);
};

FicheProduit.propTypes = {
	ficheProduit: ficheProduitPropTypes,
	prePackageOffers: PropTypes.array,
	negociated: PropTypes.array,
	allAvailableDurations: PropTypes.arrayOf(PropTypes.number),
	videos: PropTypes.arrayOf(videoPropTypes),
	mobileSlideshowPhotos: PropTypes.arrayOf(photoPropTypes),
	desktopSlideShowPhotos: PropTypes.arrayOf(photoPropTypes),
	currentShop: PropTypes.string,
	resolution: PropTypes.string,
	partnerCode: PropTypes.string,
	shouldFetchLookAlikeProduts: PropTypes.bool,
	isWhiteLabel: PropTypes.bool,
	specialOffer: PropTypes.object,
	googleAPIKey: PropTypes.string,
	showPfsMessage: PropTypes.bool,
	productPriceValue: PropTypes.string,
	updateBooking: PropTypes.func,
	hidePhoneContactUs: PropTypes.bool,
	isPaymentDescriptionBlockExist: PropTypes.bool,
	emiratesPassReinsurance: PropTypes.object,
};

export default memo(FicheProduit);
